import { getMapStopArrivalTimePillPresentation } from "../../../../theme/DelayStatusColors";
import { toReadableHex } from "../../../../tools/ColorTools";
import { resolveRouteTypeFranceBadgeForLine } from "../../../../tools/routeTypeFranceBadge.tools";
import {
    resolveStopLabelTransitRoutesForDisplay,
    STOP_LABEL_ROUTE_COLUMNS,
    STOP_LABEL_ROUTE_MAX_ROWS,
} from "../../../../tools/transitRouteDisplay.tools";
import type { GtfsRoute } from "../../../../types/Gtfs.types";
import {
    applyStopLabelCorrespondenceBadgeFixedWidth,
    STOP_LABEL_CONTENT_ROW_CLASS,
    STOP_LABEL_CORRESPONDENCE_BADGE_WIDTH_CLASS,
    STOP_LABEL_NAME_CLASS,
    STOP_LABEL_NAME_ONLY_CLASS,
} from "./stopLabel.placement";
import type { StopLabelData } from "./stopLabel.types";

/** Base Tailwind classes for arrival-time pills (radius supplied by the parent shell). */
const STOP_LABEL_ARRIVAL_TIME_PILL_BASE_CLASS =
    "inline-flex shrink-0 items-center justify-center border box-border px-1 py-px text-[9px] font-bold leading-none min-h-[14px]";

/** Vertical stack wrapper when correspondence badges are shown. */
const STOP_LABEL_TWO_LINE_STACK_CLASS = "flex flex-col w-full gap-1";

/** Thin separator between stop name row and correspondence badges. */
const STOP_LABEL_CORRESPONDENCE_DIVIDER_CLASS =
    "h-px w-full shrink-0 bg-slate-300/70 dark:bg-slate-500/50";

/** Vertical stack of correspondence badge rows (each row is horizontal). */
const STOP_LABEL_CORRESPONDENCE_ROWS_CLASS = "flex flex-col gap-0.5 w-full";

/** Single horizontal row of correspondence badges (up to four). */
const STOP_LABEL_CORRESPONDENCE_ROW_CLASS = "flex flex-row gap-0.5";

/** Shared compact route badge classes for map stop labels. */
const STOP_LABEL_ROUTE_BADGE_BASE_CLASS =
    "inline-flex flex-none box-border items-center justify-center overflow-hidden truncate rounded-full border font-bold text-[9px] leading-none text-center py-px px-0 min-h-[12px]";

/** White border around correspondence badges. */
const STOP_LABEL_ROUTE_BADGE_BORDER_COLOR = "#ffffff";

/** Ellipsis pill styling aligned with search autocomplete. */
const STOP_LABEL_ELLIPSIS_BACKGROUND = "#cecece";
const STOP_LABEL_ELLIPSIS_TEXT_COLOR = "#000000";

/** Default route badge colors when GTFS values are missing. */
const STOP_LABEL_DEFAULT_ROUTE_COLOR = "13d39c";
const STOP_LABEL_DEFAULT_ROUTE_TEXT_COLOR = "000000";

/**
 * Creates the left-aligned, ellipsis-capable stop name element.
 *
 * @param name Visible stop name.
 * @param className Tailwind classes for the name cell.
 * @returns Span element for the label row.
 */
function createStopLabelNameElement( name: string, className: string ): HTMLSpanElement {
    const nameEl = document.createElement( "span" );
    nameEl.className = className;
    nameEl.textContent = name;
    nameEl.title = name;

    return nameEl;
}

/**
 * Appends the first row (stop name and optional arrival-time pill) to a row container.
 *
 * @param rowEl Horizontal flex row element.
 * @param data Stop label presentation data.
 * @param isDarkUi Whether dark theme is currently active.
 * @param borderRadiusClass Tailwind radius utility for the arrival-time pill.
 */
function appendStopLabelPrimaryRow(
    rowEl: HTMLDivElement,
    data: StopLabelData,
    isDarkUi: boolean,
    borderRadiusClass: string,
): void {
    rowEl.className = STOP_LABEL_CONTENT_ROW_CLASS;

    if ( data.arrivalTime === null ) {
        rowEl.appendChild( createStopLabelNameElement( data.name, STOP_LABEL_NAME_ONLY_CLASS ) );

        return;
    }

    rowEl.appendChild( createStopLabelNameElement( data.name, STOP_LABEL_NAME_CLASS ) );

    const pillPresentation = getMapStopArrivalTimePillPresentation(
        isDarkUi,
        data.delaySec,
        data.hasRealtimeDelay,
        data.applyDelayColoring,
    );
    const arrivalTimeEl = document.createElement( "span" );
    arrivalTimeEl.className = `${ STOP_LABEL_ARRIVAL_TIME_PILL_BASE_CLASS } ${ borderRadiusClass }`;
    arrivalTimeEl.textContent = data.arrivalTime;
    arrivalTimeEl.style.backgroundColor = pillPresentation.backgroundColor;
    arrivalTimeEl.style.color = pillPresentation.textColor;
    arrivalTimeEl.style.borderColor = pillPresentation.borderColor;
    rowEl.appendChild( arrivalTimeEl );
}

/**
 * Creates a compact route badge element for map stop labels.
 *
 * SNCF routes with a known France product code render their product icon on a
 * white chip (logos need a light background); other routes keep the colored
 * textual badge.
 *
 * @param route GTFS route descriptor.
 * @returns Styled badge span.
 */
function createStopLabelRouteBadgeElement( route: GtfsRoute ): HTMLSpanElement {
    const badgeEl = document.createElement( "span" );
    badgeEl.className = `${ STOP_LABEL_ROUTE_BADGE_BASE_CLASS } ${ STOP_LABEL_CORRESPONDENCE_BADGE_WIDTH_CLASS }`;

    const franceBadge = resolveRouteTypeFranceBadgeForLine( route.routeTypeFrance, route.name );

    if ( franceBadge !== null ) {
        const iconEl = document.createElement( "img" );
        iconEl.src = franceBadge.icon;
        iconEl.alt = franceBadge.label;
        iconEl.className = "h-[9px] w-auto max-w-[31px] object-contain";
        iconEl.draggable = false;
        badgeEl.appendChild( iconEl );
        badgeEl.title = `${ franceBadge.label } — ${ route.name }`;
        badgeEl.style.backgroundColor = "#ffffff";
    } else {
        badgeEl.textContent = route.name;
        badgeEl.title = route.name;
        badgeEl.style.backgroundColor = toReadableHex( route.color ?? STOP_LABEL_DEFAULT_ROUTE_COLOR );
        badgeEl.style.color = toReadableHex( route.textColor ?? STOP_LABEL_DEFAULT_ROUTE_TEXT_COLOR );
    }

    badgeEl.style.borderColor = STOP_LABEL_ROUTE_BADGE_BORDER_COLOR;
    applyStopLabelCorrespondenceBadgeFixedWidth( badgeEl );

    return badgeEl;
}

/**
 * Creates the ellipsis pill shown when more routes exist than the display limit.
 *
 * @returns Styled ellipsis badge span.
 */
function createStopLabelEllipsisBadgeElement(): HTMLSpanElement {
    const ellipsisEl = document.createElement( "span" );
    ellipsisEl.className = `${ STOP_LABEL_ROUTE_BADGE_BASE_CLASS } ${ STOP_LABEL_CORRESPONDENCE_BADGE_WIDTH_CLASS }`;
    ellipsisEl.textContent = "...";
    ellipsisEl.style.backgroundColor = STOP_LABEL_ELLIPSIS_BACKGROUND;
    ellipsisEl.style.color = STOP_LABEL_ELLIPSIS_TEXT_COLOR;
    ellipsisEl.style.borderColor = STOP_LABEL_ROUTE_BADGE_BORDER_COLOR;
    applyStopLabelCorrespondenceBadgeFixedWidth( ellipsisEl );

    return ellipsisEl;
}

/**
 * Appends correspondence badge rows below the primary row divider.
 *
 * Fills each horizontal row left-to-right (up to four badges) before wrapping
 * to the next line (at most two lines).
 *
 * @param parent Stack container receiving the divider and badge rows.
 * @param routes Full correspondence route list for the stop.
 */
function appendStopLabelCorrespondenceRow(
    parent: HTMLDivElement,
    routes: readonly GtfsRoute[],
): void {
    const dividerEl = document.createElement( "div" );
    dividerEl.className = STOP_LABEL_CORRESPONDENCE_DIVIDER_CLASS;
    parent.appendChild( dividerEl );

    const { displayedRoutes, showEllipsis } = resolveStopLabelTransitRoutesForDisplay( routes );

    const rowsEl = document.createElement( "div" );
    rowsEl.className = STOP_LABEL_CORRESPONDENCE_ROWS_CLASS;

    const maxSlots = STOP_LABEL_ROUTE_COLUMNS * STOP_LABEL_ROUTE_MAX_ROWS;
    const slotCount = Math.min(
        displayedRoutes.length + ( showEllipsis ? 1 : 0 ),
        maxSlots,
    );

    for ( let rowIndex = 0; rowIndex < STOP_LABEL_ROUTE_MAX_ROWS; rowIndex += 1 ) {
        const rowStart = rowIndex * STOP_LABEL_ROUTE_COLUMNS;
        if ( rowStart >= slotCount ) {
            break;
        }

        const rowEnd = Math.min( rowStart + STOP_LABEL_ROUTE_COLUMNS, slotCount );
        const rowEl = document.createElement( "div" );
        rowEl.className = STOP_LABEL_CORRESPONDENCE_ROW_CLASS;

        for ( let slotIndex = rowStart; slotIndex < rowEnd; slotIndex += 1 ) {
            const isEllipsisSlot = showEllipsis && slotIndex === displayedRoutes.length;
            rowEl.appendChild( isEllipsisSlot
                ? createStopLabelEllipsisBadgeElement()
                : createStopLabelRouteBadgeElement( displayedRoutes[slotIndex] ) );
        }

        rowsEl.appendChild( rowEl );
    }

    parent.appendChild( rowsEl );
}

/**
 * Builds a plain-text fallback label from stop label data.
 *
 * @param data Presentation-ready stop label data.
 * @returns `"StopName HH:MM"` or just `"StopName"`.
 */
export function buildStopLabelLine1( data: StopLabelData ): string {
    if ( data.arrivalTime !== null ) {
        return `${ data.name } ${ data.arrivalTime }`;
    }

    return data.name;
}

/**
 * Populates a container with stop label content.
 *
 * Single-line when {@link StopLabelData.correspondenceRoutes} is empty; otherwise
 * stacks the name row, a gray divider, and compact correspondence badges.
 *
 * @param container Parent element to populate.
 * @param data Stop label presentation data.
 * @param isDarkUi Whether dark theme is currently active.
 * @param borderRadiusClass Tailwind radius utility for the arrival-time pill.
 */
export function appendStopLabelContent(
    container: HTMLDivElement,
    data: StopLabelData,
    isDarkUi: boolean,
    borderRadiusClass: string,
): void {
    const hasCorrespondences = data.correspondenceRoutes.length > 0;

    if ( !hasCorrespondences ) {
        container.className = `${ container.className } ${ STOP_LABEL_CONTENT_ROW_CLASS }`.trim();
        appendStopLabelPrimaryRow( container, data, isDarkUi, borderRadiusClass );

        return;
    }

    container.className = `${ container.className } ${ STOP_LABEL_TWO_LINE_STACK_CLASS }`.trim();

    const primaryRowEl = document.createElement( "div" );
    appendStopLabelPrimaryRow( primaryRowEl, data, isDarkUi, borderRadiusClass );
    container.appendChild( primaryRowEl );

    appendStopLabelCorrespondenceRow( container, data.correspondenceRoutes );
}
